import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import {
  AlertTriangle,
  Shield,
  RefreshCw,
  CheckCircle,
  XCircle,
  ExternalLink,
  Filter,
  ChevronDown,
  ChevronUp,
  Clock,
  Monitor,
  Smartphone,
  Globe,
  Bot,
  User,
  Search,
  ArrowLeft
} from 'lucide-react';
import { getConfig } from '@/config/Constants';

// Types
interface ErrorLog {
  id: string;
  error_id: string;
  error_type: string;
  error_message: string;
  source: string;
  severity: string;
  stack_trace?: string;
  component_stack?: string;
  request_url?: string;
  request_method?: string;
  user_id?: string;
  user_email?: string;
  user_agent?: string;
  browser_name?: string;
  browser_version?: string;
  os_name?: string;
  os_version?: string;
  device_type?: string;
  screen_resolution?: string;
  additional_context?: Record<string, any>;
  is_bot: boolean;
  bot_name?: string;
  is_resolved: boolean;
  resolved_at?: string;
  resolved_by?: string;
  resolution_notes?: string;
  email_sent: boolean;
  email_sent_at?: string;
  occurred_at: string;
  created_at?: string;
}

interface ErrorStats {
  total: number;
  unresolved: number;
  resolved: number;
  by_source: { client: number; server: number };
  by_origin: { bot: number; user: number };
  by_severity: { critical: number; high: number; medium: number; low: number };
}

// Severity colors and icons
const severityConfig: Record<string, { color: string; bgColor: string; label: string }> = {
  critical: { color: 'text-red-400', bgColor: 'bg-red-500/20 border-red-500/30', label: 'Critical' },
  high: { color: 'text-orange-400', bgColor: 'bg-orange-500/20 border-orange-500/30', label: 'High' },
  medium: { color: 'text-yellow-400', bgColor: 'bg-yellow-500/20 border-yellow-500/30', label: 'Medium' },
  low: { color: 'text-green-400', bgColor: 'bg-green-500/20 border-green-500/30', label: 'Low' }
};

// Error List Component
function ErrorLogsList() {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    source: '',
    severity: '',
    is_resolved: '',
    is_bot: '',
    hours: '24'
  });
  const [showFilters, setShowFilters] = useState(false);

  const limit = 20;

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const config = getConfig();
      const token = localStorage.getItem('access_token');

      const params = new URLSearchParams();
      params.append('skip', String(page * limit));
      params.append('limit', String(limit));
      if (filters.source) params.append('source', filters.source);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.is_resolved) params.append('is_resolved', filters.is_resolved);
      if (filters.is_bot) params.append('is_bot', filters.is_bot);
      if (filters.hours) params.append('hours', filters.hours);

      const response = await fetch(`${config.apiUrl}/errors/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setErrors(data.data.errors);
        setTotal(data.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const config = getConfig();
      const token = localStorage.getItem('access_token');

      const params = filters.hours ? `?hours=${filters.hours}` : '';
      const response = await fetch(`${config.apiUrl}/errors/stats${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchErrors();
    fetchStats();
  }, [page, filters]);

  const handleResolve = async (errorId: string) => {
    try {
      const config = getConfig();
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${config.apiUrl}/errors/logs/${errorId}/resolve`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolution_notes: 'Resolved via admin panel' })
      });

      if (response.ok) {
        fetchErrors();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to resolve error:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getDeviceIcon = (deviceType?: string) => {
    switch (deviceType) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Monitor className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="text-3xl font-bold text-white">{stats.total}</div>
            <div className="text-gray-400 text-sm">Total Errors</div>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <div className="text-3xl font-bold text-red-400">{stats.unresolved}</div>
            <div className="text-gray-400 text-sm">Unresolved</div>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <div className="text-3xl font-bold text-green-400">{stats.resolved}</div>
            <div className="text-gray-400 text-sm">Resolved</div>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <div className="text-3xl font-bold text-purple-400">{stats.by_severity.critical}</div>
            <div className="text-gray-400 text-sm">Critical</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 transition"
        >
          <Filter className="w-4 h-4" />
          Filters
          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showFilters && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-white/5 border border-white/10 rounded-xl">
            <select
              value={filters.hours}
              onChange={(e) => setFilters({ ...filters, hours: e.target.value })}
              className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="1">Last 1 hour</option>
              <option value="6">Last 6 hours</option>
              <option value="24">Last 24 hours</option>
              <option value="72">Last 3 days</option>
              <option value="168">Last 7 days</option>
              <option value="">All time</option>
            </select>
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="">All Sources</option>
              <option value="client">Client</option>
              <option value="server">Server</option>
            </select>
            <select
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={filters.is_resolved}
              onChange={(e) => setFilters({ ...filters, is_resolved: e.target.value })}
              className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="">All Status</option>
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
            </select>
            <select
              value={filters.is_bot}
              onChange={(e) => setFilters({ ...filters, is_bot: e.target.value })}
              className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="">All Origins</option>
              <option value="false">Users Only</option>
              <option value="true">Bots Only</option>
            </select>
          </div>
        )}
      </div>

      {/* Error List */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
            <p className="text-gray-400">Loading errors...</p>
          </div>
        ) : errors.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
            <p className="text-gray-400">No errors found</p>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {errors.map((error) => {
              const severity = severityConfig[error.severity] || severityConfig.medium;
              return (
                <div
                  key={error.error_id}
                  className="p-4 hover:bg-white/5 transition cursor-pointer"
                  onClick={() => navigate(`/admin/error-logs/${error.error_id}`)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${severity.bgColor} ${severity.color} border`}>
                          {severity.label}
                        </span>
                        <span className="text-purple-400 font-mono text-sm">{error.error_id}</span>
                        {error.is_bot && (
                          <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400 border border-gray-500/30 flex items-center gap-1">
                            <Bot className="w-3 h-3" /> Bot
                          </span>
                        )}
                        {error.is_resolved && (
                          <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Resolved
                          </span>
                        )}
                      </div>
                      <div className="text-white font-medium mb-1">{error.error_type}</div>
                      <div className="text-gray-400 text-sm truncate">{error.error_message}</div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(error.occurred_at)}
                        </span>
                        {error.browser_name && (
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {error.browser_name}
                          </span>
                        )}
                        {error.device_type && (
                          <span className="flex items-center gap-1">
                            {getDeviceIcon(error.device_type)}
                            {error.device_type}
                          </span>
                        )}
                        {error.user_email && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {error.user_email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!error.is_resolved && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolve(error.error_id);
                          }}
                          className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30 transition"
                        >
                          Resolve
                        </button>
                      )}
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="p-4 border-t border-white/10 flex items-center justify-between">
            <div className="text-gray-400 text-sm">
              Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-white disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= total}
                className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-white disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Error Detail Component
function ErrorLogDetail({ errorId }: { errorId: string }) {
  const navigate = useNavigate();
  const [error, setError] = useState<ErrorLog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchError = async () => {
      try {
        const config = getConfig();
        const token = localStorage.getItem('access_token');

        const response = await fetch(`${config.apiUrl}/errors/logs/${errorId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setError(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchError();
  }, [errorId]);

  const handleResolve = async () => {
    if (!error) return;

    try {
      const config = getConfig();
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${config.apiUrl}/errors/logs/${errorId}/resolve`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolution_notes: 'Resolved via admin panel' })
      });

      if (response.ok) {
        const data = await response.json();
        setError({ ...error, ...data.data });
      }
    } catch (err) {
      console.error('Failed to resolve error:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
        <p className="text-gray-400">Loading error details...</p>
      </div>
    );
  }

  if (!error) {
    return (
      <div className="p-8 text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-2" />
        <p className="text-gray-400">Error not found</p>
      </div>
    );
  }

  const severity = severityConfig[error.severity] || severityConfig.medium;

  return (
    <div>
      {/* Back button */}
      <button
        onClick={() => navigate('/admin/error-logs')}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Error Logs
      </button>

      {/* Header */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-lg text-sm font-medium ${severity.bgColor} ${severity.color} border`}>
                {severity.label}
              </span>
              {error.is_bot && (
                <span className="px-3 py-1 rounded-lg text-sm bg-gray-500/20 text-gray-400 border border-gray-500/30 flex items-center gap-1">
                  <Bot className="w-4 h-4" /> Bot Traffic
                </span>
              )}
              {error.is_resolved ? (
                <span className="px-3 py-1 rounded-lg text-sm bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Resolved
                </span>
              ) : (
                <span className="px-3 py-1 rounded-lg text-sm bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
                  <XCircle className="w-4 h-4" /> Unresolved
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{error.error_type}</h2>
            <div className="text-purple-400 font-mono text-lg">{error.error_id}</div>
          </div>
          {!error.is_resolved && (
            <button
              onClick={handleResolve}
              className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Mark as Resolved
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-6">
        <h3 className="text-red-400 font-semibold mb-2">Error Message</h3>
        <pre className="text-white font-mono text-sm whitespace-pre-wrap">{error.error_message}</pre>
      </div>

      {/* Details Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Request Info */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Request Information</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-400">URL</dt>
              <dd className="text-white text-sm truncate max-w-[60%]">{error.request_url || 'N/A'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Source</dt>
              <dd className="text-white capitalize">{error.source}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Occurred</dt>
              <dd className="text-white">{new Date(error.occurred_at).toLocaleString()}</dd>
            </div>
          </dl>
        </div>

        {/* User/Device Info */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">User & Device</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-400">User Email</dt>
              <dd className="text-white">{error.user_email || 'Anonymous'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Browser</dt>
              <dd className="text-white">{error.browser_name} {error.browser_version}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">OS</dt>
              <dd className="text-white">{error.os_name} {error.os_version}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Device</dt>
              <dd className="text-white capitalize">{error.device_type || 'Unknown'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Screen</dt>
              <dd className="text-white">{error.screen_resolution || 'Unknown'}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Stack Trace */}
      {error.stack_trace && (
        <div className="bg-black/50 border border-white/10 rounded-xl p-6 mb-6">
          <h3 className="text-white font-semibold mb-4">Stack Trace</h3>
          <pre className="text-green-400 font-mono text-xs overflow-x-auto whitespace-pre-wrap">{error.stack_trace}</pre>
        </div>
      )}

      {/* Component Stack */}
      {error.component_stack && (
        <div className="bg-black/50 border border-white/10 rounded-xl p-6 mb-6">
          <h3 className="text-white font-semibold mb-4">React Component Stack</h3>
          <pre className="text-blue-400 font-mono text-xs overflow-x-auto whitespace-pre-wrap">{error.component_stack}</pre>
        </div>
      )}

      {/* Additional Context */}
      {error.additional_context && Object.keys(error.additional_context).length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
          <h3 className="text-white font-semibold mb-4">Additional Context</h3>
          <dl className="space-y-3">
            {Object.entries(error.additional_context).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <dt className="text-gray-400">{key}</dt>
                <dd className="text-white text-sm max-w-[60%] truncate">{String(value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Resolution Info */}
      {error.is_resolved && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
          <h3 className="text-green-400 font-semibold mb-4">Resolution</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-400">Resolved At</dt>
              <dd className="text-white">{error.resolved_at ? new Date(error.resolved_at).toLocaleString() : 'N/A'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Resolved By</dt>
              <dd className="text-white">{error.resolved_by || 'N/A'}</dd>
            </div>
            {error.resolution_notes && (
              <div>
                <dt className="text-gray-400 mb-1">Notes</dt>
                <dd className="text-white">{error.resolution_notes}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}

// Main Component
export default function AdminErrorLogs() {
  const { errorId } = useParams();

  return (
    <AdminLayout>
      <section className="relative py-12 px-6 overflow-hidden min-h-screen">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-black to-purple-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(239, 68, 68, 0.1) 0%, transparent 50%)',
        }}></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600/20 border border-red-500/30 rounded-full text-sm mb-6">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-red-400">Error Monitoring</span>
            </div>
            <h1 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                {errorId ? 'Error Details' : 'Error Logs'}
              </span>
            </h1>
            <p className="text-xl text-gray-400">
              {errorId ? 'Full error information and stack trace' : 'Monitor and resolve application errors'}
            </p>
          </div>

          {errorId ? <ErrorLogDetail errorId={errorId} /> : <ErrorLogsList />}
        </div>
      </section>
    </AdminLayout>
  );
}
